<!-- src/lib/components/AgentStatusBar.svelte -->
<!--
  Persistent bottom strip showing running / queued / completed-today agent
  counts. Clicking the running count toggles a popover listing every active
  agent with elapsed time and a Cancel button.

  Mounted once from App.svelte (after <Toaster />). Position: fixed bottom,
  z-index 30 — above content but below drawers/modals.
-->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import { agentStore } from '../stores/agents.svelte.ts';
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';
  import { toast } from '../stores/toast.svelte.ts';

  interface Props {
    onViewAll?: () => void;
  }
  let { onViewAll }: Props = $props();

  // Panel open/closed state
  let panelOpen = $state(false);
  let runningPop = $state(false);
  let queuedPop = $state(false);
  let failedPop = $state(false);

  // Live clock — ticks every second so elapsed time updates in real time
  let now = $state(Date.now());
  const tick = setInterval(() => { now = Date.now(); }, 1000);
  let runningPopTimer: ReturnType<typeof setTimeout> | null = null;
  let queuedPopTimer: ReturnType<typeof setTimeout> | null = null;
  let failedPopTimer: ReturnType<typeof setTimeout> | null = null;
  let prevRunning = agentStore.running.length;
  let prevQueued = agentStore.queued.length;
  let prevFailed = agentStore.failedToday;

  function triggerPop(kind: 'running' | 'queued' | 'failed') {
    if (kind === 'running') {
      if (runningPopTimer) clearTimeout(runningPopTimer);
      runningPop = true;
      runningPopTimer = setTimeout(() => {
        runningPop = false;
        runningPopTimer = null;
      }, 250);
      return;
    }
    if (kind === 'queued') {
      if (queuedPopTimer) clearTimeout(queuedPopTimer);
      queuedPop = true;
      queuedPopTimer = setTimeout(() => {
        queuedPop = false;
        queuedPopTimer = null;
      }, 250);
      return;
    }
    if (failedPopTimer) clearTimeout(failedPopTimer);
    failedPop = true;
    failedPopTimer = setTimeout(() => {
      failedPop = false;
      failedPopTimer = null;
    }, 250);
  }

  $effect(() => {
    const running = agentStore.running.length;
    const queued = agentStore.queued.length;
    const failed = agentStore.failedToday;

    if (running !== prevRunning) {
      prevRunning = running;
      triggerPop('running');
    }
    if (queued !== prevQueued) {
      prevQueued = queued;
      triggerPop('queued');
    }
    if (failed !== prevFailed) {
      prevFailed = failed;
      triggerPop('failed');
    }
  });

  onDestroy(() => {
    clearInterval(tick);
    if (runningPopTimer) clearTimeout(runningPopTimer);
    if (queuedPopTimer) clearTimeout(queuedPopTimer);
    if (failedPopTimer) clearTimeout(failedPopTimer);
  });

  // Toggle the expand panel
  function togglePanel() {
    panelOpen = !panelOpen;
  }

  // Close panel when clicking outside
  function handleOutsideClick(e: MouseEvent) {
    if (!panelOpen) return;
    const target = e.target as HTMLElement;
    if (!target.closest('.agent-status-bar')) {
      panelOpen = false;
    }
  }

  // Cross-reference agent_id against pmStore boardDetails cards
  function cardForAgent(agentId: string): { title: string; human_id: string | null } | null {
    for (const bd of Object.values(pmStore.boardDetails)) {
      for (const col of bd.columns) {
        for (const card of col.cards) {
          if (card.agent_id === agentId) {
            return { title: card.title, human_id: card.human_id };
          }
        }
      }
    }
    return null;
  }

  // Compute elapsed time as a human-readable string
  function elapsed(startedAt: string): string {
    const ms = now - new Date(startedAt).getTime();
    if (ms < 0) return '0s';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return `${m}m ${rem}s`;
    const h = Math.floor(m / 60);
    const remM = m % 60;
    return `${h}h ${remM}m`;
  }

  // Cancel an agent
  async function cancelAgent(agentId: string, label: string) {
    const confirmed = window.confirm(`Stop agent working on ${label}?`);
    if (!confirmed) return;
    try {
      await api(`/api/v1/tasks/${agentId}`, { method: 'DELETE' });
      toast.push({ title: 'Agent cancelled', body: label, kind: 'warning' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.push({ title: 'Cancel failed', body: msg, kind: 'error' });
    }
  }
</script>

<svelte:window onclick={handleOutsideClick} />

<div class="agent-status-bar" role="status" aria-label="Agent status">
  <!-- Counts row -->
  <div class="bar-inner">
    <div class="bar-counts">
      <!-- Running count — clickable, opens panel -->
      <button
        class="count-pill running"
        class:active={panelOpen}
        class:pop={runningPop}
        onclick={togglePanel}
        aria-expanded={panelOpen}
        title="Click to view running agents"
      >
        <span class="dot dot-running" aria-hidden="true"></span>
        <span class="count-num">{agentStore.running.length}</span>
        <span class="count-label">running</span>
      </button>

      <!-- Queued count -->
      <span class="count-pill queued" class:pop={queuedPop}>
        <span class="dot dot-queued" aria-hidden="true"></span>
        <span class="count-num">{agentStore.queued.length}</span>
        <span class="count-label">queued</span>
      </span>

      <!-- Completed today count -->
      <span class="count-pill completed">
        <span class="glyph" aria-hidden="true">✓</span>
        <span class="count-num">{agentStore.completedToday}</span>
        <span class="count-label">today</span>
      </span>

      {#if agentStore.failedToday > 0}
        <span class="count-pill failed" class:pop={failedPop}>
          <span class="glyph fail-glyph" aria-hidden="true">✕</span>
          <span class="count-num">{agentStore.failedToday}</span>
          <span class="count-label">failed</span>
        </span>
      {/if}
    </div>

    <!-- View all button -->
    <button
      class="view-all-btn"
      onclick={() => { onViewAll?.(); panelOpen = false; }}
      title="Open Agents tab"
    >
      View all →
    </button>
  </div>

  <!-- Expand panel -->
  {#if panelOpen}
    <div class="expand-panel" role="dialog" aria-label="Running agents">
      {#if agentStore.running.length === 0}
        <p class="empty-msg">No agents running.</p>
      {:else}
        <ul class="agent-list">
          {#each agentStore.running as agent (agent.agent_id)}
            {@const card = cardForAgent(agent.agent_id)}
            {@const label = card
              ? [(card.human_id ?? ''), card.title].filter(Boolean).join(' ')
              : agent.agent_id.slice(0, 8)}
            <li class="agent-row">
              <span class="dot dot-running" aria-hidden="true"></span>

              <div class="agent-info">
                <span class="agent-id" title={agent.agent_id}>
                  {agent.agent_id.slice(0, 8)}
                </span>
                {#if card}
                  <span class="agent-card-title" title={card.title}>
                    {card.human_id ? `${card.human_id} ` : ''}{card.title.length > 40
                      ? card.title.slice(0, 40) + '…'
                      : card.title}
                  </span>
                {:else if agent.prompt}
                  <span class="agent-card-title" title={agent.prompt}>
                    {agent.prompt.length > 48
                      ? agent.prompt.slice(0, 48) + '…'
                      : agent.prompt}
                  </span>
                {/if}
              </div>

              <span class="elapsed" title="Elapsed time">{elapsed(agent.started_at)}</span>

              <button
                class="btn danger cancel-btn"
                onclick={() => cancelAgent(agent.agent_id, label)}
              >
                Cancel
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>

<style>
  .agent-status-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 28px;
    z-index: 30;
    background: oklch(0.085 0.016 275);
    border-top: 1px solid oklch(0.30 0.05 275 / 0.20);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    /* Panel opens upward — use column-reverse so panel appears above bar */
  }

  .bar-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 28px;
    padding: 0 0.75rem;
    flex-shrink: 0;
  }

  .bar-counts {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Count pills */
  .count-pill {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.7rem;
    line-height: 1;
    color: oklch(0.55 0.04 310 / 0.70);
    background: transparent;
    border: none;
    padding: 0;
    cursor: default;
  }

  /* Running pill is a button */
  button.count-pill {
    cursor: pointer;
    padding: 0.2rem 0.45rem;
    border-radius: 4px;
    border: 1px solid transparent;
    transition: background 100ms ease, border-color 100ms ease, color 100ms ease;
  }
  button.count-pill:hover,
  button.count-pill.active {
    background: oklch(0.12 0.02 275 / 0.80);
    border-color: oklch(0.30 0.05 275 / 0.30);
    color: oklch(0.85 0.06 310 / 0.90);
  }

  /* Dots */
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-block;
  }
  .dot-running {
    background: oklch(0.72 0.19 145);
    box-shadow: 0 0 4px oklch(0.72 0.19 145 / 0.60);
    animation: run-pulse 1.5s ease-in-out infinite;
  }
  @keyframes run-pulse {
    0%, 100% { opacity: 1;    box-shadow: 0 0 4px oklch(0.72 0.19 145 / 0.60); }
    50%       { opacity: 0.55; box-shadow: 0 0 2px oklch(0.72 0.19 145 / 0.25); }
  }
  .dot-queued {
    background: oklch(0.78 0.16 85);
    box-shadow: 0 0 4px oklch(0.78 0.16 85 / 0.45);
  }

  .count-num {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    font-weight: 600;
    color: oklch(0.82 0.05 310 / 0.90);
  }
  .count-label {
    font-size: 0.66rem;
    color: oklch(0.50 0.03 310 / 0.65);
  }

  .glyph {
    font-size: 0.68rem;
    color: oklch(0.50 0.03 310 / 0.55);
  }
  .fail-glyph {
    font-size: 0.62rem;
    color: oklch(0.62 0.22 25 / 0.75);
  }

  /* Colorize the running pill counts */
  .running .count-num { color: oklch(0.82 0.15 145 / 0.95); }
  .queued  .count-num { color: oklch(0.82 0.14 85  / 0.90); }
  .failed {
    padding: 0.2rem 0.45rem;
    border-radius: 4px;
    background: oklch(0.20 0.04 25 / 0.55);
    border: 1px solid oklch(0.62 0.22 25 / 0.22);
  }
  .failed .count-num { color: oklch(0.72 0.24 25 / 0.95); }
  .failed .count-label { color: oklch(0.72 0.18 25 / 0.72); }

  @keyframes count-pop {
    0% { transform: scale(1); }
    40% { transform: scale(1.35); }
    100% { transform: scale(1); }
  }
  .count-pill.pop .count-num {
    animation: count-pop 250ms ease-out forwards;
  }

  /* View all button */
  .view-all-btn {
    background: transparent;
    border: none;
    color: oklch(0.50 0.12 265 / 0.70);
    font-size: 0.68rem;
    cursor: pointer;
    padding: 0.2rem 0.35rem;
    border-radius: 3px;
    transition: color 100ms ease, background 100ms ease;
    font-family: inherit;
    white-space: nowrap;
  }
  .view-all-btn:hover {
    color: oklch(0.75 0.18 265 / 0.95);
    background: oklch(0.12 0.02 275 / 0.70);
  }

  /* ── Expand panel ─────────────────────────────────── */
  .expand-panel {
    position: absolute;
    bottom: 100%;  /* opens upward above the bar */
    left: 0;
    min-width: 340px;
    max-width: 480px;
    background: oklch(0.095 0.018 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.30);
    border-bottom: none;
    border-radius: 8px 8px 0 0;
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.45);
    padding: 0.5rem 0;
    z-index: 31;
  }

  .empty-msg {
    padding: 0.65rem 1rem;
    font-size: 0.76rem;
    color: oklch(0.45 0.03 310 / 0.65);
    font-style: italic;
    margin: 0;
  }

  .agent-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  .agent-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.85rem;
    border-bottom: 1px solid oklch(0.20 0.025 275 / 0.35);
    transition: background 100ms ease;
  }
  .agent-row:last-child { border-bottom: none; }
  .agent-row:hover { background: oklch(0.12 0.02 275 / 0.60); }

  .agent-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
  }

  .agent-id {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.68rem;
    color: oklch(0.72 0.28 310 / 0.70);
    letter-spacing: 0.02em;
  }

  .agent-card-title {
    font-size: 0.72rem;
    color: oklch(0.82 0.05 310 / 0.85);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .elapsed {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.66rem;
    color: oklch(0.55 0.04 310 / 0.65);
    white-space: nowrap;
    flex-shrink: 0;
  }

  .cancel-btn {
    flex-shrink: 0;
    font-size: 0.68rem;
    padding: 0.22rem 0.55rem;
  }

  @media (prefers-reduced-motion: reduce) {
    .dot-running { animation: none; }
  }
</style>
